import { useState, useCallback } from 'react';
import { abandonedCartApi } from '../api/abandonedCart';
import type { AbandonedCartRecovery, GenerateAbandonedCartPayload } from '../api/abandonedCart';

export function useAbandonedCart() {
  const [recoveries, setRecoveries] = useState<AbandonedCartRecovery[]>([]);
  const [activeRecovery, setActiveRecovery] = useState<AbandonedCartRecovery | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchRecoveries = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await abandonedCartApi.list(p);
      setRecoveries(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load abandoned cart recoveries.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GenerateAbandonedCartPayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await abandonedCartApi.generate(payload);
      const recovery = res.data.data;
      setActiveRecovery(recovery);
      setRecoveries((prev) => [recovery, ...prev]);
      return recovery;
    } catch (err: any) {
      const msg = err?.message ?? 'Abandoned cart recovery generation failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteRecovery = useCallback(async (id: number) => {
    try {
      await abandonedCartApi.destroy(id);
      setRecoveries((prev) => prev.filter((r) => r.id !== id));
      if (activeRecovery?.id === id) setActiveRecovery(null);
    } catch {
      setError('Failed to delete recovery.');
    }
  }, [activeRecovery]);

  return {
    recoveries, activeRecovery, loading, generating, error, total, page, lastPage,
    fetchRecoveries, generate, deleteRecovery, setActiveRecovery, setError,
  };
}
